// src/client.ts
var ApiError = class extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
  status;
  code;
  details;
};
var ApiClient = class {
  constructor(options) {
    this.options = options;
    this.fetchImpl = options.fetch ?? ((...args) => fetch(...args));
  }
  options;
  refreshing;
  fetchImpl;
  get baseUrl() {
    return this.options.baseUrl.replace(/\/$/, "");
  }
  url(path, params, query) {
    let p = path.replace(/\{(\w+)\}/g, (_, k) => {
      const v = params?.[k];
      if (v === void 0) throw new Error(`Missing path param "${k}" for ${path}`);
      return encodeURIComponent(v);
    });
    const qs = Object.entries(query ?? {}).filter(([, v]) => v !== void 0 && v !== null && v !== "").map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join("&");
    if (qs) p += `?${qs}`;
    return `${this.baseUrl}${p}`;
  }
  /**
   * Typed call: `client.typed<Dto>()('get', '/path/{id}', { params })`. Curried so TypeScript infers the path and
   * method (checked against the OpenAPI spec) while the response type is given explicitly.
   */
  typed() {
    return (method, path, opts = {}) => this.request(method, path, opts);
  }
  async request(method, path, opts = {}) {
    const res = await this.send(method, path, opts);
    if (opts.raw) return res;
    if (res.status === 204) return void 0;
    const text = await res.text();
    return text ? JSON.parse(text) : void 0;
  }
  async send(method, path, opts) {
    const headers = {};
    const token = this.options.storage.getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (this.options.mode === "native") headers["X-Client"] = "native";
    if (opts.body !== void 0) headers["Content-Type"] = "application/json";
    const res = await this.fetchImpl(this.url(path, opts.params, opts.query), {
      method: method.toUpperCase(),
      headers,
      body: opts.form ?? (opts.body !== void 0 ? JSON.stringify(opts.body) : void 0),
      credentials: this.options.mode === "cookie" ? "include" : "omit",
      signal: opts.signal
    });
    if (res.status === 401 && !opts.noRetry && !path.startsWith("/auth/")) {
      if (await this.refresh()) return this.send(method, path, { ...opts, noRetry: true });
      this.options.onAuthFailure?.();
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(res.status, body.error?.code ?? `HTTP_${res.status}`, body.error?.message ?? res.statusText, body.error?.details);
    }
    return res;
  }
  /** Single-flight: concurrent 401s share one refresh call. Resolves true when a new access token is stored. */
  async refresh() {
    return await this.refreshDetailed() === "ok";
  }
  /**
   * Like refresh(), but tells "session invalid" apart from "server unreachable", so apps can keep the user
   * signed in (and retry) through a restart or a network blip instead of showing the login screen.
   */
  refreshDetailed() {
    this.refreshing ??= (async () => {
      try {
        const { storage, mode } = this.options;
        const refreshToken = mode === "native" ? await storage.getRefreshToken?.() : void 0;
        if (mode === "native" && !refreshToken) return "invalid";
        const res = await this.fetchImpl(`${this.baseUrl}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...mode === "native" ? { "X-Client": "native" } : {} },
          body: JSON.stringify(mode === "native" ? { refreshToken } : {}),
          credentials: mode === "cookie" ? "include" : "omit"
        });
        if (res.status >= 500) return "offline";
        if (!res.ok) {
          storage.setAccessToken(null);
          await storage.setRefreshToken?.(null);
          return "invalid";
        }
        await this.store(await res.json());
        return "ok";
      } catch {
        return "offline";
      } finally {
        setTimeout(() => this.refreshing = void 0, 0);
      }
    })();
    return this.refreshing;
  }
  async store(auth) {
    this.options.storage.setAccessToken(auth.accessToken);
    if (auth.refreshToken) await this.options.storage.setRefreshToken?.(auth.refreshToken);
  }
};

// src/api.ts
function createApi(c) {
  return {
    auth: {
      async login(input) {
        const res = await c.typed()("post", "/auth/login", { body: input });
        await c.store(res);
        return res;
      },
      async logout() {
        const refreshToken = c.options.mode === "native" ? await c.options.storage.getRefreshToken?.() : void 0;
        await c.typed()("post", "/auth/logout", { body: refreshToken ? { refreshToken } : {} }).catch(() => void 0);
        c.options.storage.setAccessToken(null);
        await c.options.storage.setRefreshToken?.(null);
      },
      me: () => c.typed()("get", "/auth/me"),
      refresh: () => c.refresh(),
      refreshDetailed: () => c.refreshDetailed()
    },
    users: {
      list: () => c.typed()("get", "/users"),
      create: (input) => c.typed()("post", "/users", { body: input }),
      update: (id, input) => c.typed()("patch", "/users/{id}", { params: { id }, body: input })
    },
    conversations: {
      list: (q = {}) => c.typed()("get", "/conversations", { query: q }),
      get: (id) => c.typed()("get", "/conversations/{id}", { params: { id } }),
      messages: (id, q = {}) => c.typed()("get", "/conversations/{id}/messages", { params: { id }, query: q }),
      reply: (id, input) => c.typed()("post", "/conversations/{id}/messages", { params: { id }, body: input }),
      /** Picture reply: a JPEG/PNG file, or the URL of one of our images (menu photo). */
      sendImage(id, input) {
        const form = new FormData();
        if (input.file) form.append("file", input.file, input.filename ?? "image.jpg");
        if (input.imageUrl) form.append("imageUrl", input.imageUrl);
        if (input.caption) form.append("caption", input.caption);
        return c.typed()("post", "/conversations/{id}/image", { params: { id }, form });
      },
      sendTemplate: (id, input) => c.typed()("post", "/conversations/{id}/template", { params: { id }, body: input }),
      markRead: (id) => c.typed()("post", "/conversations/{id}/read", { params: { id } }),
      assign: (id, userId) => c.typed()("post", "/conversations/{id}/assign", { params: { id }, body: { userId } }),
      /** Latest suggestions still relevant for the chat, or null. */
      suggestions: (id) => c.typed()("get", "/conversations/{id}/suggestions", { params: { id } }).then((r) => r.current),
      suggestReplies: (id) => c.typed()("post", "/conversations/{id}/suggest-replies", { params: { id } }),
      extractOrder: (id) => c.typed()("post", "/conversations/{id}/extract-order", { params: { id } }),
      summary: (id) => c.typed()("post", "/conversations/{id}/summary", { params: { id } })
    },
    whatsapp: {
      numbers: () => c.typed()("get", "/whatsapp/numbers")
    },
    customers: {
      profile: (id) => c.typed()("get", "/customers/{id}/profile", { params: { id } }),
      search: (q = {}) => c.typed()("get", "/customers", { query: q }),
      create: (input) => c.typed()("post", "/customers", { body: input }),
      import: (input) => c.typed()("post", "/customers/import", { body: input }),
      setMarketing: (id, optIn) => c.typed()("put", "/customers/{id}/marketing", { params: { id }, body: { optIn } }),
      update: (id, input) => c.typed()("patch", "/customers/{id}", { params: { id }, body: input })
    },
    media: {
      /** Returns a Blob (fetched with auth; use URL.createObjectURL on web). */
      async blob(mediaId) {
        const res = await c.typed()("get", "/media/{mediaId}", { params: { mediaId }, raw: true });
        return res.blob();
      },
      url: (mediaId) => c.url("/media/{mediaId}", { mediaId })
    },
    aiRuns: {
      reject: (id) => c.typed()("post", "/ai-runs/{id}/reject", { params: { id } })
    },
    settings: {
      get: () => c.typed()("get", "/settings"),
      update: (s) => c.typed()("put", "/settings", { body: s })
    },
    templates: {
      list: () => c.typed()("get", "/templates"),
      create: (input) => c.typed()("post", "/templates", { body: input }),
      remove: (name) => c.typed()("delete", "/templates/{name}", { params: { name } }),
      sync: () => c.typed()("post", "/templates/sync"),
      mappings: () => c.typed()("get", "/notification-templates"),
      setMapping: (input) => c.typed()("put", "/notification-templates", { body: input }),
      removeMapping: (status) => c.typed()("delete", "/notification-templates/{status}", { params: { status } })
    },
    menu: {
      list: () => c.typed()("get", "/menu"),
      createCategory: (input) => c.typed()("post", "/menu/categories", { body: input }),
      updateCategory: (id, input) => c.typed()("patch", "/menu/categories/{id}", { params: { id }, body: input }),
      deleteCategory: (id) => c.typed()("delete", "/menu/categories/{id}", { params: { id } }),
      reorderCategories: (ids) => c.typed()("put", "/menu/categories/order", { body: { ids } }),
      createItem: (input) => c.typed()("post", "/menu/items", { body: input }),
      updateItem: (id, input) => c.typed()("put", "/menu/items/{id}", { params: { id }, body: input }),
      setAvailability: (id, available) => c.typed()("patch", "/menu/items/{id}/availability", { params: { id }, body: { available } }),
      archiveItem: (id) => c.typed()("delete", "/menu/items/{id}", { params: { id } }),
      setDietTags: (id, dietTags) => c.typed()("put", "/menu/items/{id}/diet-tags", { params: { id }, body: { dietTags } }),
      /** AI tags untagged dishes (all=true: every unreviewed dish). Runs in the background. */
      autoTag: (all = false) => c.typed()("post", "/menu/diet-tags/auto", { body: { all } }),
      reorderItems: (ids) => c.typed()("put", "/menu/items/order", { body: { ids } }),
      uploadImage(file, filename = "image.jpg") {
        const form = new FormData();
        form.append("file", file, filename);
        return c.typed()("post", "/menu/images", { form });
      }
    },
    specials: {
      list: (date) => c.typed()("get", "/specials", { query: { date } }),
      set: (date, input) => c.typed()("put", "/specials/{date}", { params: { date }, body: input })
    },
    campaigns: {
      list: () => c.typed()("get", "/campaigns"),
      get: (id) => c.typed()("get", "/campaigns/{id}", { params: { id } }),
      create: (input) => c.typed()("post", "/campaigns", { body: input }),
      update: (id, input) => c.typed()("put", "/campaigns/{id}", { params: { id }, body: input }),
      remove: (id) => c.typed()("delete", "/campaigns/{id}", { params: { id } }),
      preview: (kind, audience) => c.typed()("post", "/campaigns/preview", { body: { kind, audience } }),
      suggestedAudience: (date) => c.typed()("get", "/campaigns/suggested-audience", { query: { date } }),
      send: (id, scheduledAt) => c.typed()("post", "/campaigns/{id}/send", { params: { id }, body: { scheduledAt: scheduledAt ?? null } })
    },
    orders: {
      list: (q = {}) => c.typed()("get", "/orders", { query: q }),
      get: (id) => c.typed()("get", "/orders/{id}", { params: { id } }),
      create: (input) => c.typed()("post", "/orders", { body: input }),
      update: (id, input) => c.typed()("patch", "/orders/{id}", { params: { id }, body: input }),
      transition: (id, to, reason) => c.typed()("post", "/orders/{id}/transition", { params: { id }, body: { to, reason } }),
      /** Sends the customer receipt PDF to the order's WhatsApp chat. */
      sendReceipt: (id) => c.typed()("post", "/orders/{id}/receipt", { params: { id } })
    },
    analytics: {
      summary: (r) => c.typed()("get", "/analytics/summary", { query: r }),
      timeseries: (r) => c.typed()("get", "/analytics/timeseries", { query: r }),
      topItems: (r) => c.typed()("get", "/analytics/top-items", { query: r }),
      heatmap: (r) => c.typed()("get", "/analytics/heatmap", { query: r }),
      ai: (r) => c.typed()("get", "/analytics/ai", { query: r }),
      menuInsights: () => c.typed()("get", "/analytics/menu-insights"),
      insights: () => c.typed()("get", "/analytics/insights"),
      generateInsights: () => c.typed()("post", "/analytics/insights/generate")
    },
    devices: {
      register: (input) => c.typed()("post", "/devices", { body: input }),
      unregister: (token) => c.typed()("delete", "/devices/{token}", { params: { token } })
    }
  };
}

// src/socket.ts
import { RT } from "@restaurant/shared";
import { io } from "socket.io-client";
function connectRealtime(opts) {
  const u = new URL(opts.baseUrl, globalThis.location?.href);
  const s = io(u.origin, {
    path: `${u.pathname.replace(/\/$/, "")}/socket.io`,
    auth: (cb) => cb({ token: opts.getToken() }),
    transports: ["websocket"],
    reconnectionDelay: 1e3,
    reconnectionDelayMax: 1e4
  });
  const h = opts.handlers;
  s.on("connect", () => h.onConnectionChange?.(true));
  s.on("disconnect", () => h.onConnectionChange?.(false));
  s.on("connect_error", async (err) => {
    h.onConnectionChange?.(false);
    if (err.message === "unauthorized" && await opts.refresh()) s.connect();
  });
  if (h.onMessage) s.on(RT.messageNew, h.onMessage);
  if (h.onMessageStatus) s.on(RT.messageStatus, h.onMessageStatus);
  if (h.onConversation) s.on(RT.conversationUpdated, h.onConversation);
  if (h.onOrderCreated) s.on(RT.orderCreated, h.onOrderCreated);
  if (h.onOrderUpdated) s.on(RT.orderUpdated, h.onOrderUpdated);
  if (h.onAiDraft) s.on(RT.aiDraft, h.onAiDraft);
  if (h.onAiSuggestions) s.on(RT.aiSuggestions, h.onAiSuggestions);
  if (h.onTemplateStatus) s.on(RT.templateStatus, h.onTemplateStatus);
  return s;
}
export {
  ApiClient,
  ApiError,
  connectRealtime,
  createApi
};
